import Instense from "./Axios-config";
import { API_ROOT } from "./Api-config";

const baseUrl = `${API_ROOT.backendHost}/Documents`;

export interface Document {
  id: number;
  documentName: string;
  documentNumber?: string;
  isDocumentNumberAutoGenerated?: boolean;
  description?: string;
  documentType: string;
  categoryId?: number;
  categoryName?: string;
  fileExtension?: string;
  mimeType?: string;
  createdBy: number;
  createdDate: string;
  modifiedBy?: number;
  modifiedDate?: string;
  tags?: string;
  relatedEntityType?: string;
  relatedEntityId?: number;
  requiresVersionControl: boolean;
  currentVersionNumber?: number;
  currentVersionId?: number;
  fileSize: number;
  fileName?: string;
  filePath?: string;
}

export interface DocumentVersion {
  id: number;
  versionNumber: number;
  fileName: string;
  fileSize: number;
  uploadedBy: number;
  uploadedDate: string;
  versionNotes?: string;
  isCurrentVersion: boolean;
}

export interface DocumentCategory {
  id: number;
  categoryName: string;
  description?: string;
}

export interface DocumentsResponse {
  documents: Document[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface VersionsResponse {
  versions: DocumentVersion[];
}

export interface CategoriesResponse {
  categories: DocumentCategory[];
}

export class DocumentService {
  /**
   * Get all documents with pagination and filters
   */
  static async GetDocuments(
    categoryId?: number,
    relatedEntityType?: string,
    relatedEntityId?: number,
    search?: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<DocumentsResponse> {
    const params: any = { page, pageSize };
    if (categoryId) params.categoryId = categoryId;
    if (relatedEntityType) params.relatedEntityType = relatedEntityType;
    if (relatedEntityId) params.relatedEntityId = relatedEntityId;
    if (search) params.search = search;

    const response = await Instense.get<DocumentsResponse>(baseUrl, {
      params,
    });

    return response.data;
  }

  /**
   * Get a single document by ID
   */
  static async GetDocument(id: number): Promise<Document> {
    const response = await Instense.get<{ result: Document }>(`${baseUrl}/${id}`);
    return response.data.result;
  }

  /**
   * Upload a new document
   */
  static async UploadDocument(
    file: File,
    documentName: string,
    description?: string,
    categoryId?: number,
    requiresVersionControl: boolean = false,
    tags?: string,
    relatedEntityType?: string,
    relatedEntityId?: number,
    documentNumber?: string
  ): Promise<{ id: number; message: string }> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("documentName", documentName);
    if (description) formData.append("description", description);
    if (categoryId) formData.append("categoryId", categoryId.toString());
    formData.append("requiresVersionControl", requiresVersionControl.toString());
    if (tags) formData.append("tags", tags);
    if (relatedEntityType) formData.append("relatedEntityType", relatedEntityType);
    if (relatedEntityId) formData.append("relatedEntityId", relatedEntityId.toString());
    if (documentNumber) formData.append("documentNumber", documentNumber);

    const response = await Instense.post<{ id: number; message: string }>(
      `${baseUrl}/upload`,
      formData
    );

    return response.data;
  }

  /**
   * Upload a new version for a versioned document
   */
  static async UploadVersion(
    documentId: number,
    file: File,
    versionNotes?: string
  ): Promise<{ versionId: number; versionNumber: number; message: string }> {
    const formData = new FormData();
    formData.append("file", file);
    if (versionNotes) formData.append("versionNotes", versionNotes);

    const response = await Instense.post<{ versionId: number; versionNumber: number; message: string }>(
      `${baseUrl}/${documentId}/upload-version`,
      formData
    );

    return response.data;
  }

  /**
   * Get all versions for a document
   */
  static async GetVersions(documentId: number): Promise<DocumentVersion[]> {
    const response = await Instense.get<VersionsResponse>(
      `${baseUrl}/${documentId}/versions`
    );
    return response.data.versions;
  }

  /**
   * Download a document
   */
  static async DownloadDocument(documentId: number, versionId?: number): Promise<Blob> {
    const params: any = {};
    if (versionId) params.versionId = versionId;

    const response = await Instense.get(`${baseUrl}/${documentId}/download`, {
      params,
      responseType: "blob",
    });

    return response.data;
  }

  /**
   * Download a specific version
   */
  static async DownloadVersion(documentId: number, versionId: number): Promise<Blob> {
    const response = await Instense.get(
      `${baseUrl}/${documentId}/download-version/${versionId}`,
      {
        responseType: "blob",
      }
    );

    return response.data;
  }

  /**
   * Update document metadata
   */
  static async UpdateDocument(
    id: number,
    documentName?: string,
    description?: string,
    categoryId?: number,
    tags?: string,
    documentNumber?: string
  ): Promise<{ id: number; message: string }> {
    const response = await Instense.put<{ id: number; message: string }>(
      `${baseUrl}/${id}`,
      {
        documentName,
        description,
        categoryId,
        tags,
        documentNumber,
      }
    );

    return response.data;
  }

  /**
   * Delete a document (soft delete)
   */
  static async DeleteDocument(id: number): Promise<{ message: string }> {
    const response = await Instense.delete<{ message: string }>(
      `${baseUrl}/${id}`
    );

    return response.data;
  }

  /**
   * Get all categories
   */
  static async GetCategories(): Promise<DocumentCategory[]> {
    const response = await Instense.get<CategoriesResponse>(
      `${baseUrl}/categories`
    );
    return response.data.categories;
  }

  /**
   * Create a new category
   */
  static async CreateCategory(
    categoryName: string,
    description?: string
  ): Promise<{ id: number; message: string }> {
    const response = await Instense.post<{ id: number; message: string }>(
      `${baseUrl}/categories`,
      {
        categoryName,
        description,
      }
    );

    return response.data;
  }

  /**
   * Get download URL for a document
   */
  static getDownloadUrl(documentId: number, versionId?: number): string {
    const params = versionId ? `?versionId=${versionId}` : "";
    return `${baseUrl}/${documentId}/download${params}`;
  }

  /**
   * Format file size for display
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  }

  /**
   * Get file icon based on file extension or mime type
   */
  static getFileIcon(fileExtension?: string, mimeType?: string): string {
    if (!fileExtension && !mimeType) return "fa-file";

    const ext = fileExtension?.toLowerCase() || "";
    const mime = mimeType?.toLowerCase() || "";

    if (mime.startsWith("image/")) return "fa-file-image";
    if (mime.startsWith("application/pdf")) return "fa-file-pdf";
    if (mime.includes("word") || ext === ".doc" || ext === ".docx") return "fa-file-word";
    if (mime.includes("excel") || ext === ".xls" || ext === ".xlsx") return "fa-file-excel";
    if (mime.includes("powerpoint") || ext === ".ppt" || ext === ".pptx") return "fa-file-powerpoint";
    if (mime.includes("zip") || ext === ".zip" || ext === ".rar") return "fa-file-archive";
    if (mime.includes("text") || ext === ".txt") return "fa-file-alt";

    return "fa-file";
  }
}

